import { useState, useEffect } from 'react';

/**
 * usePWA — handles service worker registration and install prompt
 *
 * Returns:
 *   - isInstallable:  boolean — browser supports install prompt
 *   - isInstalled:    boolean — running as standalone PWA
 *   - isOffline:      boolean — no internet connection
 *   - promptInstall:  fn      — triggers the browser install dialog
 *   - swRegistered:   boolean
 *   - updateAvailable: boolean — a new SW is waiting to take over
 *   - applyUpdate:    fn      — tells the waiting SW to skipWaiting + reload
 */
const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => window.__pwaInstallEvent || null
  );
  const [isInstallable, setIsInstallable] = useState(
    () => !!window.__pwaInstallEvent
  );
  const [isInstalled, setIsInstalled] = useState(
    () => window.matchMedia('(display-mode: standalone)').matches
  );
  const [isOffline, setIsOffline]       = useState(!navigator.onLine);
  const [swRegistered, setSwRegistered] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    // ── Service Worker Registration ─────────────────────
    if ('serviceWorker' in navigator) {
      // [FEAT-PWA 2026-05-23] updateViaCache:'none' — never reuse the
      // HTTP cache for /sw.js itself. Without this, some browsers
      // serve a stale SW file from disk and the new install logic
      // (precache list, fetch handlers) never reaches the user.
      navigator.serviceWorker
        .register('/sw.js', { scope: '/', updateViaCache: 'none' })
        .then((reg) => {
          console.log('[EduGate] SW registered:', reg.scope);
          setSwRegistered(true);

          // If a new SW is already waiting on first load, surface the update
          if (reg.waiting) {
            setWaitingWorker(reg.waiting);
            setUpdateAvailable(true);
          }

          // Watch for newly installing workers
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (!newWorker) return;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // A new SW is installed and an old one is still controlling — offer update.
                setWaitingWorker(newWorker);
                setUpdateAvailable(true);
              }
            });
          });
        })
        .catch((err) => console.error('[EduGate] SW registration failed:', err));

      // When a new SW takes over, reload once so the page is served by it.
      // [AUDIT-2026-09-22] …but only when it REPLACES an older one. sw.js calls
      // clients.claim() on activate, which fires controllerchange on a page
      // that had no controller at all — a first visit — and that reload made
      // every first visit load the whole dashboard twice (every API call
      // doubled, measured from the server log). A first visit is already
      // served from the network; there is nothing stale to replace.
      const hadController = !!navigator.serviceWorker.controller;
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController || reloading) return;
        reloading = true;
        window.location.reload();
      });
    }

    // ── Install prompt (arriving after React mounts) ─────
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.__pwaInstallEvent = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // ── Custom event fired by the early-capture script ───
    const handlePwaInstallable = () => {
      const saved = window.__pwaInstallEvent;
      if (saved) {
        setDeferredPrompt(saved);
        setIsInstallable(true);
      }
    };
    window.addEventListener('pwa-installable', handlePwaInstallable);

    // ── Already installed as PWA? ────────────────────────
    const mq = window.matchMedia('(display-mode: standalone)');
    const handleMq = (e) => setIsInstalled(e.matches);
    mq.addEventListener('change', handleMq);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.__pwaInstallEvent = null;
    });

    // ── Network status ───────────────────────────────────
    const goOnline  = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('pwa-installable', handlePwaInstallable);
      mq.removeEventListener('change', handleMq);
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const promptInstall = async () => {
    const prompt = deferredPrompt || window.__pwaInstallEvent;
    if (!prompt) {
      console.warn('[EduGate PWA] No install prompt available');
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    console.log('[EduGate PWA] User choice:', outcome);
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.__pwaInstallEvent = null;
    }
  };

  const applyUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return {
    isInstallable,
    isInstalled,
    isOffline,
    promptInstall,
    swRegistered,
    updateAvailable,
    applyUpdate,
  };
};

export default usePWA;
